import {
  externalConnectionControllerGetSharePointMyDrive,
  externalConnectionControllerListSharePointDrives,
  externalConnectionControllerListSharePointSites,
  externalFileControllerBrowseFiles,
} from "@/lib/gen-api";
import { useCallback, useState } from "react";
import { toast } from "sonner";

type SharePointBrowseRoot = "sites" | "myFiles";
type SharePointBrowsePhase = "browseRoot" | "site" | "drive" | "folder";

export type SharePointBrowseRow = {
  id: string;
  name: string;
  mimeType?: string;
  size?: number;
  modifiedTime?: string;
  parents?: string[];
  isFolder?: boolean;
};

type UseSharePointFileBrowseParams = {
  onRowsChange: (rows: SharePointBrowseRow[]) => void;
  onLoadingChange: (loading: boolean) => void;
  onClearFileSelection: () => void;
};

export function useSharePointFileBrowse({
  onRowsChange,
  onLoadingChange,
  onClearFileSelection,
}: UseSharePointFileBrowseParams) {
  const [browseRoot, setBrowseRoot] = useState<SharePointBrowseRoot | null>(
    null,
  );
  const [phase, setPhase] = useState<SharePointBrowsePhase>("browseRoot");
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedSiteName, setSelectedSiteName] = useState<string | null>(null);
  const [selectedDriveId, setSelectedDriveId] = useState<string | null>(null);
  const [selectedDriveName, setSelectedDriveName] = useState<string | null>(
    null,
  );
  const [folderPath, setFolderPath] = useState<
    { folderId: string; folderName: string }[]
  >([]);

  const reset = useCallback(() => {
    setBrowseRoot(null);
    setPhase("browseRoot");
    setSelectedSiteId(null);
    setSelectedSiteName(null);
    setSelectedDriveId(null);
    setSelectedDriveName(null);
    setFolderPath([]);
    onClearFileSelection();
    onRowsChange([]);
  }, [onClearFileSelection, onRowsChange]);

  const loadSites = useCallback(
    async (connectionUuid: string, siteSearch?: string) => {
      onLoadingChange(true);
      try {
        const response = await externalConnectionControllerListSharePointSites({
          path: { connectionUuid },
          query: { search: siteSearch ?? "" },
        });
        const sites = response.data?.sites ?? [];
        onRowsChange(
          sites.map((s) => ({
            id: s.id,
            name: s.displayName,
            isFolder: true,
          })),
        );
      } catch (error) {
        toast.error("Failed to load SharePoint sites");
        console.error(error);
      } finally {
        onLoadingChange(false);
      }
    },
    [onLoadingChange, onRowsChange],
  );

  const loadDrives = useCallback(
    async (connectionUuid: string, siteId: string) => {
      onLoadingChange(true);
      try {
        const response = await externalConnectionControllerListSharePointDrives(
          {
            path: { connectionUuid, siteId },
          },
        );
        const drives = response.data?.drives ?? [];
        onRowsChange(
          drives.map((d) => ({
            id: d.id,
            name: d.name,
            isFolder: true,
          })),
        );
      } catch (error) {
        toast.error("Failed to load document libraries");
        console.error(error);
      } finally {
        onLoadingChange(false);
      }
    },
    [onLoadingChange, onRowsChange],
  );

  const loadFolderChildren = useCallback(
    async (
      connectionUuid: string,
      driveId: string,
      folderId?: string,
      searchTerm?: string,
    ) => {
      onLoadingChange(true);
      try {
        const response = await externalFileControllerBrowseFiles({
          path: { connectionUuid },
          query: {
            folderId: folderId ?? "",
            search: searchTerm ?? "",
            driveId,
          },
        });
        onRowsChange(response.data?.files ?? []);
      } catch (error) {
        toast.error("Failed to load files");
        console.error("Failed to load files:", error);
      } finally {
        onLoadingChange(false);
      }
    },
    [onLoadingChange, onRowsChange],
  );

  const enterFolderBrowse = useCallback(
    async (connectionUuid: string, driveId: string, driveName: string) => {
      setSelectedDriveId(driveId);
      setSelectedDriveName(driveName);
      setPhase("folder");
      setFolderPath([]);
      onClearFileSelection();
      await loadFolderChildren(connectionUuid, driveId);
    },
    [loadFolderChildren, onClearFileSelection],
  );

  const startConnection = useCallback(() => {
    setPhase("browseRoot");
    onRowsChange([]);
  }, [onRowsChange]);

  const selectBrowseRoot = useCallback(
    async (connectionUuid: string, root: SharePointBrowseRoot) => {
      setBrowseRoot(root);
      onClearFileSelection();

      if (root === "sites") {
        setPhase("site");
        setSelectedSiteId(null);
        setSelectedSiteName(null);
        await loadSites(connectionUuid);
        return;
      }

      onLoadingChange(true);
      try {
        const response = await externalConnectionControllerGetSharePointMyDrive(
          {
            path: { connectionUuid },
          },
        );
        const drive = response.data?.drive;
        if (!drive?.id) {
          throw new Error("Personal drive not available");
        }
        setSelectedSiteId(null);
        setSelectedSiteName(null);
        await enterFolderBrowse(connectionUuid, drive.id, drive.name);
      } catch (error) {
        toast.error("Failed to load My files");
        console.error(error);
        setBrowseRoot(null);
        setPhase("browseRoot");
      } finally {
        onLoadingChange(false);
      }
    },
    [enterFolderBrowse, loadSites, onClearFileSelection, onLoadingChange],
  );

  const navigateIntoRow = useCallback(
    async (connectionUuid: string, row: SharePointBrowseRow) => {
      if (!row.isFolder) return;

      if (phase === "site") {
        setSelectedSiteId(row.id);
        setSelectedSiteName(row.name);
        setPhase("drive");
        await loadDrives(connectionUuid, row.id);
        return;
      }
      if (phase === "drive") {
        await enterFolderBrowse(connectionUuid, row.id, row.name);
      }
    },
    [enterFolderBrowse, loadDrives, phase],
  );

  const navigateIntoFolder = useCallback(
    async (
      connectionUuid: string,
      row: SharePointBrowseRow,
      search: string,
    ) => {
      if (!selectedDriveId) return;
      onClearFileSelection();
      setFolderPath((prev) => [
        ...prev,
        { folderId: row.id, folderName: row.name },
      ]);
      await loadFolderChildren(connectionUuid, selectedDriveId, row.id, search);
    },
    [loadFolderChildren, onClearFileSelection, selectedDriveId],
  );

  const reloadCurrentView = useCallback(
    async (connectionUuid: string, searchTerm: string) => {
      if (phase === "browseRoot") {
        return;
      }
      if (phase === "site") {
        await loadSites(connectionUuid, searchTerm);
        return;
      }
      if (phase === "drive" && selectedSiteId) {
        await loadDrives(connectionUuid, selectedSiteId);
        return;
      }
      if (phase === "folder" && selectedDriveId) {
        const currentFolderId =
          folderPath.length > 0
            ? folderPath[folderPath.length - 1].folderId
            : undefined;
        await loadFolderChildren(
          connectionUuid,
          selectedDriveId,
          currentFolderId,
          searchTerm,
        );
      }
    },
    [
      folderPath,
      loadDrives,
      loadFolderChildren,
      loadSites,
      phase,
      selectedDriveId,
      selectedSiteId,
    ],
  );

  const goBack = useCallback(
    async (connectionUuid: string, search: string): Promise<boolean> => {
      if (phase === "folder" && folderPath.length > 0) {
        const newPath = folderPath.slice(0, -1);
        setFolderPath(newPath);
        onClearFileSelection();
        const parentId =
          newPath.length > 0 ? newPath[newPath.length - 1].folderId : undefined;
        if (selectedDriveId) {
          await loadFolderChildren(
            connectionUuid,
            selectedDriveId,
            parentId,
            search,
          );
        }
        return false;
      }
      if (phase === "folder") {
        if (browseRoot === "myFiles") {
          setPhase("browseRoot");
          setBrowseRoot(null);
          setSelectedDriveId(null);
          setSelectedDriveName(null);
          onRowsChange([]);
          return false;
        }
        setPhase("drive");
        setFolderPath([]);
        onClearFileSelection();
        if (selectedSiteId) {
          await loadDrives(connectionUuid, selectedSiteId);
        }
        return false;
      }
      if (phase === "drive") {
        setPhase("site");
        setSelectedDriveId(null);
        setSelectedDriveName(null);
        await loadSites(connectionUuid, search);
        return false;
      }
      if (phase === "site") {
        setPhase("browseRoot");
        setBrowseRoot(null);
        setSelectedSiteId(null);
        setSelectedSiteName(null);
        onRowsChange([]);
        return false;
      }
      // Safe guard for new phases
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (phase === "browseRoot") {
        reset();
        return true;
      }
      return true;
    },
    [
      browseRoot,
      folderPath,
      loadDrives,
      loadFolderChildren,
      loadSites,
      onClearFileSelection,
      onRowsChange,
      phase,
      reset,
      selectedDriveId,
      selectedSiteId,
    ],
  );

  const currentFolder =
    folderPath.length > 0 ? folderPath[folderPath.length - 1] : null;

  const isBrowseRootPhase = phase === "browseRoot";
  const isFolderPhase = phase === "folder";
  const isPickerPhase =
    phase === "site" || phase === "drive" || phase === "browseRoot";

  return {
    browseRoot,
    phase,
    selectedSiteId,
    selectedSiteName,
    selectedDriveId,
    selectedDriveName,
    folderPath,
    setFolderPath,
    currentFolder,
    isBrowseRootPhase,
    isFolderPhase,
    isPickerPhase,
    reset,
    startConnection,
    selectBrowseRoot,
    navigateIntoRow,
    navigateIntoFolder,
    reloadCurrentView,
    goBack,
    loadFolderChildren,
  };
}
